'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import AutomationCard from '@/components/AutomationCard';
import MiniTable from '@/components/MiniTable';
import StatusBadge from '@/components/StatusBadge';
import ProgressBar from '@/components/ProgressBar';

const SparklineChart = dynamic(() => import('@/app/components/SparklineChart'), { ssr: false });
const DonutChart = dynamic(() => import('@/app/components/DonutChart'), { ssr: false });

// ─── Card 1: Emergency Call Intake ───────────────────────────────────────────
const emergencyCalls = [
  { id: 'ec-001', customer: 'Nguyen, T.', issue: 'No A/C — 108°F', priority: 'P1', technician: 'Torres, J.', status: 'Dispatched' },
  { id: 'ec-002', customer: 'Kim, S.', issue: 'Compressor failure', priority: 'P1', technician: 'Reyes, M.', status: 'En Route' },
  { id: 'ec-003', customer: 'Delgado, R.', issue: 'No cooling — elderly', priority: 'P1', technician: 'Park, D.', status: 'On Site' },
  { id: 'ec-004', customer: 'Williams, B.', issue: 'Freon leak — smell', priority: 'P2', technician: 'Patel, A.', status: 'Assigned' },
  { id: 'ec-005', customer: 'Martinez, C.', issue: 'Blower motor failure', priority: 'P2', technician: 'Chen, L.', status: 'Scheduled' },
  { id: 'ec-006', customer: 'Hernandez, G.', issue: 'Thermostat issue', priority: 'P3', technician: 'Unassigned', status: 'Queued' },
];

// ─── Card 2: Estimate Recovery ────────────────────────────────────────────────
const estimateRows = [
  { id: 'est-001', customer: 'Chen, B.', value: '$22,100', probability: 88, lastContact: '6h ago', nextFollowUp: 'Tomorrow 9AM', stage: 'Seq 3/5' },
  { id: 'est-002', customer: 'Park, L.', value: '$16,800', probability: 74, lastContact: '1d ago', nextFollowUp: 'Today 4PM', stage: 'Seq 2/5' },
  { id: 'est-003', customer: 'Johnson, R.', value: '$14,200', probability: 61, lastContact: '2d ago', nextFollowUp: 'Tomorrow 2PM', stage: 'Seq 4/5' },
  { id: 'est-004', customer: 'Smith, A.', value: '$19,400', probability: 45, lastContact: '3d ago', nextFollowUp: 'Thu 10AM', stage: 'Seq 2/5' },
  { id: 'est-005', customer: 'Garcia, M.', value: '$11,800', probability: 38, lastContact: '5d ago', nextFollowUp: 'Fri 11AM', stage: 'Seq 5/5' },
];

// ─── Card 3: Maintenance Renewals ─────────────────────────────────────────────
const renewalData = [
  { id: 'rn-001', label: 'Booked', value: 412, pct: 64, color: 'success' as const },
  { id: 'rn-002', label: 'Pending', value: 184, pct: 29, color: 'warning' as const },
  { id: 'rn-003', label: 'Lost', value: 46, pct: 7, color: 'danger' as const },
];

// ─── Card 4: Dispatcher Copilot ───────────────────────────────────────────────
const routeRows = [
  { id: 'rt-001', technician: 'Torres, J.', stops: 9, miles: 41, revenue: '$7,820', efficiency: 96 },
  { id: 'rt-002', technician: 'Reyes, M.', stops: 8, miles: 38, revenue: '$6,940', efficiency: 92 },
  { id: 'rt-003', technician: 'Park, D.', stops: 7, miles: 29, revenue: '$5,460', efficiency: 89 },
  { id: 'rt-004', technician: 'Patel, A.', stops: 8, miles: 44, revenue: '$7,200', efficiency: 84 },
  { id: 'rt-005', technician: 'Chen, L.', stops: 6, miles: 22, revenue: '$4,880', efficiency: 91 },
  { id: 'rt-006', technician: 'Williams, K.', stops: 9, miles: 51, revenue: '$8,100', efficiency: 79 },
];

// ─── Card 7: Technician Performance ──────────────────────────────────────────
const techRows = [
  { id: 'tp-001', tech: 'Torres, J.', revPerHr: '$284', rating: '4.9★', upsell: '68%', callQuality: 94, coaching: 'None' },
  { id: 'tp-002', tech: 'Reyes, M.', revPerHr: '$261', rating: '4.8★', upsell: '54%', callQuality: 88, coaching: 'None' },
  { id: 'tp-003', tech: 'Park, D.', revPerHr: '$248', rating: '4.7★', upsell: '49%', callQuality: 86, coaching: 'None' },
  { id: 'tp-004', tech: 'Patel, A.', revPerHr: '$219', rating: '4.6★', upsell: '31%', callQuality: 79, coaching: 'IAQ Upsell' },
  { id: 'tp-005', tech: 'Chen, L.', revPerHr: '$198', rating: '4.5★', upsell: '28%', callQuality: 74, coaching: 'Close Rate' },
];

// ─── Card 8: AR Recovery ──────────────────────────────────────────────────────
const arRows = [
  { id: 'ar-001', customer: 'Martinez, C.', balance: '$2,840', days: 47, probability: 92, action: 'Collected' },
  { id: 'ar-002', customer: 'Johnson, R.', balance: '$4,200', days: 34, probability: 78, action: 'Plan Agreed' },
  { id: 'ar-003', customer: 'Williams, T.', balance: '$8,100', days: 61, probability: 44, action: 'Final Notice' },
  { id: 'ar-004', customer: 'Davis, M.', balance: '$3,420', days: 28, probability: 81, action: 'Follow-up SMS' },
  { id: 'ar-005', customer: 'Anderson, P.', balance: '$1,980', days: 19, probability: 88, action: 'Auto-Reminder' },
];

// ─── Card 9: Warranty Registration ────────────────────────────────────────────
const warrantyRows = [
  { id: 'wr-001', manufacturer: 'Carrier', serial: 'CR2024-44812', customer: 'Rodriguez, A.', status: 'Registered' },
  { id: 'wr-002', manufacturer: 'Lennox', serial: 'LX2024-88412', customer: 'Delgado, M.', status: 'Registered' },
  { id: 'wr-003', manufacturer: 'Trane', serial: 'TR2024-21098', customer: 'Kim, S.', status: 'Pending' },
  { id: 'wr-004', manufacturer: 'Carrier', serial: 'CR2024-55129', customer: 'Nguyen, T.', status: 'Registered' },
  { id: 'wr-005', manufacturer: 'York', serial: 'YK2024-77341', customer: 'Park, L.', status: 'Pending' },
];

// ─── Card 10: Permit Tracker ──────────────────────────────────────────────────
const permitRows = [
  { id: 'pt-001', permit: 'P-2291', city: 'Anaheim', type: 'Residential Install', status: 'Approved', updated: '11:02 PM' },
  { id: 'pt-002', permit: 'P-2288', city: 'Irvine', type: 'Commercial HVAC', status: 'Inspection Sched.', updated: '3:14 PM' },
  { id: 'pt-003', permit: 'P-2279', city: 'Costa Mesa', type: 'Residential Install', status: 'Correction Req.', updated: '9:44 PM' },
  { id: 'pt-004', permit: 'P-2274', city: 'Tustin', type: 'Ductwork Replace', status: 'Submitted', updated: '1:22 PM' },
  { id: 'pt-005', permit: 'P-2261', city: 'Santa Ana', type: 'Residential Install', status: 'Approved', updated: '8:15 AM' },
];

// ─── Card 11: Financing ────────────────────────────────────────────────────────
const financingRows = [
  { id: 'fin-001', customer: 'Yamamoto, K.', lender: 'GoodLeap', amount: '$14,800', rate: '0% / 18mo', status: 'Approved' },
  { id: 'fin-002', customer: 'Williams, T.', lender: 'Synchrony', amount: '$9,200', rate: 'Deferred 6mo', status: 'Approved' },
  { id: 'fin-003', customer: 'Garcia, M.', lender: 'GoodLeap', amount: '$18,400', rate: '5.9% / 84mo', status: 'Approved' },
  { id: 'fin-004', customer: 'Brown, D.', lender: 'GoodLeap', amount: '$12,600', rate: '0% / 18mo', status: 'Pending Docs' },
  { id: 'fin-005', customer: 'Lee, J.', lender: 'Synchrony', amount: '$7,800', rate: 'Deferred 12mo', status: 'Approved' },
];

// ─── Card 12: IAQ Opportunities ───────────────────────────────────────────────
const iaqData = [
  { id: 'iaq-uv', label: 'UV Purification', homes: 38, value: '$68,400', avg: '$1,800' },
  { id: 'iaq-filt', label: 'Media Filtration', homes: 27, value: '$48,600', avg: '$1,800' },
  { id: 'iaq-hum', label: 'Whole-Home Humidifier', homes: 18, value: '$39,600', avg: '$2,200' },
  { id: 'iaq-air', label: 'Air Purification System', homes: 10, value: '$29,000', avg: '$2,900' },
];

// ─── Card 13: Commercial Contracts ────────────────────────────────────────────
const commercialRows = [
  { id: 'cc-001', account: 'Irvine Co. Office Park', annualValue: '$42,000', renewal: 'Aug 15', probability: 94, status: 'Active' },
  { id: 'cc-002', customer: 'South Coast Plaza', annualValue: '$28,800', renewal: 'Sep 1', probability: 88, status: 'Active' },
  { id: 'cc-003', customer: 'UCI Medical Center', annualValue: '$64,000', renewal: 'Oct 1', probability: 96, status: 'Active' },
  { id: 'cc-004', customer: 'Spectrum Center', annualValue: '$18,400', renewal: 'Jul 15', probability: 72, status: 'At Risk' },
  { id: 'cc-005', customer: 'Newport Beach Hotel', annualValue: '$31,200', renewal: 'Aug 1', probability: 85, status: 'Active' },
];

// ─── Card 16: Communication Hub ────────────────────────────────────────────────
const commData = [
  { id: 'ch-phone', channel: 'Phone', conversations: 142, booked: 56, rate: '39%' },
  { id: 'ch-sms', channel: 'SMS', conversations: 218, booked: 71, rate: '33%' },
  { id: 'ch-email', channel: 'Email', conversations: 94, booked: 28, rate: '30%' },
  { id: 'ch-chat', channel: 'Web Chat', conversations: 28, booked: 9, rate: '32%' },
];

// ─── Card 17: Marketing Attribution ──────────────────────────────────────────
const mktgRows = [
  { id: 'mk-001', source: 'Google Ads', leads: 48, revenue: '$32,400', cpl: '$84', roi: '4.8x' },
  { id: 'mk-002', source: 'SEO / Organic', leads: 31, revenue: '$22,100', cpl: '$0', roi: '∞' },
  { id: 'mk-003', source: 'Referral', leads: 28, revenue: '$19,800', cpl: '$0', roi: '∞' },
  { id: 'mk-004', source: 'Facebook Ads', leads: 22, revenue: '$8,200', cpl: '$112', roi: '1.9x' },
  { id: 'mk-005', source: 'Yelp Ads', leads: 13, revenue: '$3,500', cpl: '$94', roi: '1.4x' },
];

// ─── Card 18: Customer Retention Engine ──────────────────────────────────────
const retentionRows = [
  { id: 'ret-001', customer: 'Hoffman, B.', lastService: '14 months', campaign: 'Stage 3', probability: 74, nextAction: 'Offer call scheduled' },
  { id: 'ret-002', customer: 'Nguyen, P.', lastService: '18 months', campaign: 'Stage 2', probability: 61, nextAction: 'SMS coupon sent' },
  { id: 'ret-003', customer: 'Davis, K.', lastService: '22 months', campaign: 'Stage 4', probability: 48, nextAction: 'Final outreach' },
  { id: 'ret-004', customer: 'Thompson, S.', lastService: '11 months', campaign: 'Stage 1', probability: 82, nextAction: 'Membership offer' },
  { id: 'ret-005', customer: 'Wilson, C.', lastService: '16 months', campaign: 'Stage 3', probability: 66, nextAction: 'Estimate follow-up' },
];

const sparklineData = [
  { v: 42 }, { v: 48 }, { v: 44 }, { v: 52 }, { v: 49 }, { v: 58 }, { v: 46 },
];

export default function AutomationCardsGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4">

      {/* Card 1: Emergency Call Intake */}
      <AutomationCard
        id="card-emergency"
        title="Emergency Call Intake"
        owner="Emergency Response AI"
        status="active"
        primaryMetric="47"
        primaryLabel="requests processed today"
        description="Triages inbound emergency calls, assigns priority levels P1–P3, dispatches nearest available technician, and sends customer ETA via SMS."
        footerMetrics={[
          { label: 'P1 Dispatched', value: '12', color: 'danger' },
          { label: 'Avg Response', value: '18 min', color: 'success' },
          { label: 'Unassigned', value: '1', color: 'warning' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'customer', label: 'Customer', width: '22%' },
            { key: 'issue', label: 'Issue', width: '28%' },
            { key: 'priority', label: 'Pri', width: '8%', render: (v) => (
              <span className={`text-xs font-bold ${v === 'P1' ? 'text-danger' : v === 'P2' ? 'text-warning' : 'text-muted-foreground'}`}>{String(v)}</span>
            )},
            { key: 'technician', label: 'Technician', width: '22%' },
            { key: 'status', label: 'Status', width: '20%', render: (v) => {
              const s = String(v);
              const variant = s === 'Dispatched' || s === 'En Route' ? 'active' : s === 'On Site' ? 'complete' : s === 'Assigned' ? 'pending' : 'idle';
              return <StatusBadge variant={variant} label={s} />;
            }},
          ]}
          rows={emergencyCalls}
          maxRows={5}
        />
      </AutomationCard>

      {/* Card 2: Estimate Recovery Engine */}
      <AutomationCard
        id="card-estimate"
        title="Estimate Recovery Engine"
        owner="Sales Follow-up AI"
        status="running"
        primaryMetric="$1.82M"
        primaryLabel="pending estimate pipeline"
        description="Runs automated 5-touch follow-up sequences via phone, SMS, and email for all unsold estimates. Scores probability and escalates high-value leads."
        footerMetrics={[
          { label: 'Recovered Today', value: '$38,420', color: 'success' },
          { label: 'Active Sequences', value: '142' },
          { label: 'Avg Probability', value: '61%', color: 'warning' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'customer', label: 'Customer', width: '22%' },
            { key: 'value', label: 'Value', width: '16%', align: 'right' },
            { key: 'probability', label: 'Prob%', width: '12%', align: 'right', render: (v) => (
              <span className={`text-xs font-semibold font-tabular ${Number(v) >= 70 ? 'text-success' : Number(v) >= 50 ? 'text-warning' : 'text-danger'}`}>{String(v)}%</span>
            )},
            { key: 'lastContact', label: 'Last Touch', width: '18%' },
            { key: 'stage', label: 'Stage', width: '16%' },
          ]}
          rows={estimateRows}
          maxRows={5}
        />
      </AutomationCard>

      {/* Card 3: Maintenance Renewals */}
      <AutomationCard
        id="card-renewals"
        title="Maintenance Membership Renewals"
        owner="Membership Retention AI"
        status="running"
        primaryMetric="642"
        primaryLabel="customers in renewal pipeline"
        description="Automates renewal outreach for all expiring maintenance memberships. Handles SMS, email, and inbound calls with booking capability."
        footerMetrics={[
          { label: 'Renewal Rate', value: '84%', color: 'success' },
          { label: 'Projected Revenue', value: '$146,918', color: 'success' },
          { label: 'Lost This Month', value: '46', color: 'danger' },
        ]}
      >
        <div className="space-y-3">
          {renewalData.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground w-28 flex-shrink-0">{r.label}</span>
              <div className="flex-1">
                <ProgressBar value={r.value} max={642} color={r.color} size="md" />
              </div>
              <span className="text-xs font-semibold font-tabular text-foreground w-8 text-right">{r.value}</span>
            </div>
          ))}
          <div className="pt-2 border-t border-border/50">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Avg membership value</span>
              <span className="font-semibold text-foreground">$229/year</span>
            </div>
          </div>
        </div>
      </AutomationCard>

      {/* Card 4: Dispatcher Copilot */}
      <AutomationCard
        id="card-dispatch"
        title="Dispatcher Copilot"
        owner="Route Optimization AI"
        status="active"
        primaryMetric="91"
        primaryLabel="routes optimized today"
        description="Continuously resequences technician routes based on traffic, job duration, priority calls, and customer proximity. Reallocates techs on job completion."
        footerMetrics={[
          { label: 'Drive Time Saved', value: '4.7 hrs', color: 'success' },
          { label: 'Fuel Saved', value: '$184', color: 'success' },
          { label: 'Avg Efficiency', value: '88%' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'technician', label: 'Technician', width: '30%' },
            { key: 'stops', label: 'Stops', width: '12%', align: 'right' },
            { key: 'miles', label: 'Miles', width: '12%', align: 'right' },
            { key: 'revenue', label: 'Revenue', width: '20%', align: 'right' },
            { key: 'efficiency', label: 'Eff%', width: '14%', align: 'right', render: (v) => (
              <span className={`text-xs font-semibold font-tabular ${Number(v) >= 90 ? 'text-success' : Number(v) >= 80 ? 'text-warning' : 'text-danger'}`}>{String(v)}%</span>
            )},
          ]}
          rows={routeRows}
          maxRows={5}
        />
      </AutomationCard>

      {/* Card 5: Install Scheduling */}
      <AutomationCard
        id="card-install"
        title="Install Scheduling AI"
        owner="Install Coordination AI"
        status="active"
        primaryMetric="18"
        primaryLabel="active installs coordinated"
        description="Orchestrates all moving parts of residential and commercial installs: warehouse pull, permit status, customer readiness, equipment delivery, crew scheduling, and inspection booking."
        footerMetrics={[
          { label: 'On Schedule', value: '14', color: 'success' },
          { label: 'Delayed', value: '3', color: 'warning' },
          { label: 'Blocked', value: '1', color: 'danger' },
        ]}
      >
        <div className="space-y-2">
          {[
            { id: 'ins-wh', label: 'Warehouse Pulls', done: 16, total: 18, color: 'success' as const },
            { id: 'ins-pm', label: 'Permits Approved', done: 14, total: 18, color: 'success' as const },
            { id: 'ins-cust', label: 'Customer Confirmed', done: 17, total: 18, color: 'success' as const },
            { id: 'ins-eq', label: 'Equipment Delivered', done: 13, total: 18, color: 'warning' as const },
            { id: 'ins-cr', label: 'Crew Assigned', done: 16, total: 18, color: 'success' as const },
            { id: 'ins-insp', label: 'Inspection Booked', done: 11, total: 18, color: 'warning' as const },
          ].map((item) => (
            <div key={item.id} className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground w-36 flex-shrink-0">{item.label}</span>
              <div className="flex-1">
                <ProgressBar value={item.done} max={item.total} color={item.color} />
              </div>
              <span className="text-xs font-tabular text-foreground w-8 text-right">{item.done}/{item.total}</span>
            </div>
          ))}
        </div>
      </AutomationCard>

      {/* Card 6: Inventory Intelligence */}
      <AutomationCard
        id="card-inventory"
        title="Inventory Intelligence"
        owner="Supply Chain AI"
        status="active"
        primaryMetric="6"
        primaryLabel="purchase orders generated today"
        description="Monitors warehouse stock levels, supplier lead times, and seasonal demand forecasting. Auto-generates POs when reorder points are triggered."
        footerMetrics={[
          { label: 'PO Value', value: '$48,200' },
          { label: 'July Forecast', value: '+34%', color: 'warning' },
          { label: 'Low Stock Items', value: '8', color: 'warning' },
        ]}
      >
        <div className="space-y-2">
          {[
            { id: 'inv-r22', item: 'R-410A Refrigerant (30lb)', stock: 12, min: 20, status: 'Low' },
            { id: 'inv-cap', item: 'Dual-Run Capacitors 45+5', stock: 34, min: 25, status: 'OK' },
            { id: 'inv-flt', item: '16x25x1 Air Filters', stock: 88, min: 50, status: 'OK' },
            { id: 'inv-mot', item: 'Blower Motors 1/2 HP', stock: 6, min: 12, status: 'Critical' },
            { id: 'inv-con', item: 'Contactors 40A 24V', stock: 19, min: 15, status: 'OK' },
          ].map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-2 py-1 border-b border-border/40 last:border-0">
              <span className="text-xs text-foreground truncate flex-1">{item.item}</span>
              <span className={`text-xs font-semibold font-tabular ${item.status === 'Critical' ? 'text-danger' : item.status === 'Low' ? 'text-warning' : 'text-success'}`}>
                {item.stock} units
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${item.status === 'Critical' ? 'bg-danger/10 text-danger' : item.status === 'Low' ? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'}`}>
                {item.status}
              </span>
            </div>
          ))}
        </div>
      </AutomationCard>

      {/* Card 7: Technician Performance */}
      <AutomationCard
        id="card-techperf"
        title="Technician Performance Intelligence"
        owner="Performance Analytics AI"
        status="complete"
        primaryMetric="326"
        primaryLabel="calls reviewed today"
        description="Analyzes every completed service call for revenue per hour, upsell rate, customer satisfaction, and call quality. Generates personalized coaching recommendations."
        footerMetrics={[
          { label: 'Coaching Triggered', value: '4', color: 'warning' },
          { label: 'Top Rev/Hr', value: '$284', color: 'success' },
          { label: 'Avg Rating', value: '4.7★', color: 'success' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'tech', label: 'Technician', width: '26%' },
            { key: 'revPerHr', label: 'Rev/Hr', width: '16%', align: 'right' },
            { key: 'rating', label: 'Rating', width: '14%', align: 'right' },
            { key: 'upsell', label: 'Upsell%', width: '14%', align: 'right' },
            { key: 'coaching', label: 'Coaching', width: '20%', render: (v) => (
              <span className={`text-xs ${v === 'None' ? 'text-muted-foreground' : 'text-warning font-medium'}`}>{String(v)}</span>
            )},
          ]}
          rows={techRows}
          maxRows={5}
        />
      </AutomationCard>

      {/* Card 8: AR Recovery */}
      <AutomationCard
        id="card-ar"
        title="Accounts Receivable Recovery"
        owner="Collections AI"
        status="running"
        primaryMetric="$74,820"
        primaryLabel="total outstanding AR"
        description="Manages multi-stage collections outreach for all past-due invoices. Sends reminders, negotiates payment plans, and escalates accounts over 60 days."
        footerMetrics={[
          { label: 'Collected Today', value: '$9,460', color: 'success' },
          { label: 'Plans Agreed', value: '3' },
          { label: '60+ Days', value: '$18,200', color: 'danger' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'customer', label: 'Customer', width: '24%' },
            { key: 'balance', label: 'Balance', width: '18%', align: 'right' },
            { key: 'days', label: 'Days', width: '10%', align: 'right', render: (v) => (
              <span className={`text-xs font-tabular font-semibold ${Number(v) >= 60 ? 'text-danger' : Number(v) >= 30 ? 'text-warning' : 'text-foreground'}`}>{String(v)}</span>
            )},
            { key: 'probability', label: 'Prob%', width: '12%', align: 'right' },
            { key: 'action', label: 'Action', width: '24%', render: (v) => (
              <span className={`text-xs ${String(v) === 'Collected' ? 'text-success font-medium' : String(v) === 'Final Notice' ? 'text-danger' : 'text-foreground'}`}>{String(v)}</span>
            )},
          ]}
          rows={arRows}
          maxRows={5}
        />
      </AutomationCard>

      {/* Card 9: Warranty Registration */}
      <AutomationCard
        id="card-warranty"
        title="Warranty Registration"
        owner="Warranty Processing AI"
        status="complete"
        primaryMetric="22"
        primaryLabel="systems registered today"
        description="Automatically files manufacturer warranty registrations for all installed equipment within 24 hours of job completion. Maintains serial number database."
        footerMetrics={[
          { label: 'Registered', value: '18', color: 'success' },
          { label: 'Pending', value: '4', color: 'warning' },
          { label: 'Avg Filing Time', value: '4.2 hrs' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'manufacturer', label: 'Mfr', width: '20%' },
            { key: 'serial', label: 'Serial #', width: '30%' },
            { key: 'customer', label: 'Customer', width: '28%' },
            { key: 'status', label: 'Status', width: '18%', render: (v) => (
              <StatusBadge variant={v === 'Registered' ? 'complete' : 'pending'} label={String(v)} />
            )},
          ]}
          rows={warrantyRows}
          maxRows={5}
        />
      </AutomationCard>

      {/* Card 10: Permit Tracker */}
      <AutomationCard
        id="card-permits"
        title="Permit Tracker"
        owner="Permit Management AI"
        status="active"
        primaryMetric="31"
        primaryLabel="active permits tracked"
        description="Monitors permit status across all OC municipalities, auto-submits applications, schedules inspections, and flags correction requests for immediate human review."
        footerMetrics={[
          { label: 'Approved', value: '14', color: 'success' },
          { label: 'Corrections Req.', value: '3', color: 'danger' },
          { label: 'Avg Approval', value: '8.2 days' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'permit', label: 'Permit #', width: '18%' },
            { key: 'city', label: 'City', width: '20%' },
            { key: 'type', label: 'Type', width: '28%' },
            { key: 'status', label: 'Status', width: '26%', render: (v) => {
              const s = String(v);
              const variant = s === 'Approved' ? 'approved' : s.includes('Correction') ? 'correction' : s.includes('Inspection') ? 'pending' : 'submitted';
              return <StatusBadge variant={variant} label={s} />;
            }},
          ]}
          rows={permitRows}
          maxRows={5}
        />
      </AutomationCard>

      {/* Card 11: Financing Follow-up */}
      <AutomationCard
        id="card-financing"
        title="Financing Follow-up"
        owner="Financing Conversion AI"
        status="running"
        primaryMetric="18"
        primaryLabel="approved customers in pipeline"
        description="Manages post-approval financing conversion for GoodLeap and Synchrony approvals. Follows up with approved customers to close pending installs."
        footerMetrics={[
          { label: 'Revenue Potential', value: '$214,000', color: 'success' },
          { label: 'Converted Today', value: '4', color: 'success' },
          { label: 'Avg Loan Size', value: '$11,889' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'customer', label: 'Customer', width: '24%' },
            { key: 'lender', label: 'Lender', width: '20%' },
            { key: 'amount', label: 'Amount', width: '18%', align: 'right' },
            { key: 'rate', label: 'Terms', width: '22%' },
            { key: 'status', label: 'Status', width: '16%', render: (v) => (
              <StatusBadge variant={v === 'Approved' ? 'approved' : 'pending'} label={String(v)} />
            )},
          ]}
          rows={financingRows}
          maxRows={5}
        />
      </AutomationCard>

      {/* Card 12: IAQ Opportunities */}
      <AutomationCard
        id="card-iaq"
        title="Indoor Air Quality Opportunities"
        owner="IAQ Revenue AI"
        status="active"
        primaryMetric="93"
        primaryLabel="qualified homes identified"
        description="Analyzes service call data and home profiles to identify IAQ upgrade opportunities. Generates personalized proposals and schedules follow-up conversations."
        footerMetrics={[
          { label: 'Projected Revenue', value: '$186,000', color: 'success' },
          { label: 'Proposals Sent', value: '34' },
          { label: 'Avg Opportunity', value: '$2,000' },
        ]}
      >
        <div className="space-y-3">
          {iaqData.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/40 last:border-0">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-foreground">{item.label}</div>
                <div className="text-xs text-muted-foreground">{item.homes} homes · avg {item.avg}</div>
              </div>
              <span className="text-sm font-semibold text-success font-tabular">{item.value}</span>
            </div>
          ))}
        </div>
      </AutomationCard>

      {/* Card 13: Commercial Contracts */}
      <AutomationCard
        id="card-commercial"
        title="Commercial Maintenance Contracts"
        owner="Commercial Account AI"
        status="active"
        primaryMetric="48"
        primaryLabel="active commercial contracts"
        description="Manages commercial maintenance contract renewals, service scheduling, and account health scoring. Flags at-risk accounts for human relationship management."
        footerMetrics={[
          { label: 'Annual Portfolio', value: '$1.84M', color: 'success' },
          { label: 'At Risk', value: '3', color: 'danger' },
          { label: 'Avg Contract', value: '$38,400' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'account', label: 'Account', width: '32%', render: (v, row) => (
              <span className="text-xs text-foreground">{String(v ?? row['customer'] ?? '')}</span>
            )},
            { key: 'annualValue', label: 'Annual', width: '20%', align: 'right' },
            { key: 'renewal', label: 'Renewal', width: '18%' },
            { key: 'probability', label: 'Prob%', width: '12%', align: 'right', render: (v) => (
              <span className={`text-xs font-semibold font-tabular ${Number(v) >= 85 ? 'text-success' : Number(v) >= 70 ? 'text-warning' : 'text-danger'}`}>{String(v)}%</span>
            )},
            { key: 'status', label: 'Status', width: '16%', render: (v) => (
              <StatusBadge variant={v === 'Active' ? 'active' : 'warning'} label={String(v)} />
            )},
          ]}
          rows={commercialRows}
          maxRows={5}
        />
      </AutomationCard>

      {/* Card 14: Weather Forecast Intelligence */}
      <AutomationCard
        id="card-weather"
        title="Weather Forecast Intelligence"
        owner="Demand Forecasting AI"
        status="warning"
        primaryMetric="108°F"
        primaryLabel="forecast high tomorrow"
        description="Monitors NWS and Weather.gov feeds for OC heat events. Automatically adjusts staffing recommendations, inventory pre-positioning, and customer outreach."
        footerMetrics={[
          { label: 'Expected Calls', value: '127', color: 'danger' },
          { label: 'Normal Baseline', value: '82', color: 'default' },
          { label: 'Surge Techs Added', value: '+4', color: 'warning' },
        ]}
      >
        <div className="space-y-3">
          <div className="bg-danger/10 border border-danger/20 rounded-md p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-danger text-xs font-semibold">⚠ Heat Advisory Active</span>
            </div>
            <p className="text-xs text-foreground">Excessive heat warning through Friday. Expected 54% surge in emergency no-cooling calls.</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'wf-thu', day: 'Thu Jul 2', high: '108°F', calls: '127', color: 'text-danger' },
              { id: 'wf-fri', day: 'Fri Jul 3', high: '106°F', calls: '119', color: 'text-danger' },
              { id: 'wf-sat', day: 'Sat Jul 4', high: '104°F', calls: '108', color: 'text-warning' },
            ].map((d) => (
              <div key={d.id} className="bg-muted/50 rounded-md p-2 text-center">
                <div className="text-xs text-muted-foreground">{d.day}</div>
                <div className={`text-base font-bold font-tabular ${d.color}`}>{d.high}</div>
                <div className="text-xs text-muted-foreground">{d.calls} calls est.</div>
              </div>
            ))}
          </div>
          <SparklineChart data={sparklineData} />
        </div>
      </AutomationCard>

      {/* Card 15: Executive KPI Report */}
      <AutomationCard
        id="card-exec"
        title="Executive KPI Report"
        owner="Reporting AI"
        status="pending"
        primaryMetric="9:30 PM"
        primaryLabel="scheduled delivery tonight"
        description="Compiles daily executive summary with revenue, sales, collections, technician rankings, membership growth, and rolling 30-day forecast. Delivered via email to ownership."
        footerMetrics={[
          { label: 'Recipients', value: '4 executives' },
          { label: 'Last Delivered', value: 'Yesterday 9:31 PM' },
          { label: 'Avg Open Rate', value: '100%', color: 'success' },
        ]}
      >
        <div className="space-y-2">
          {[
            { id: 'rpt-rev', section: 'Revenue Summary', status: 'Ready', note: '$58,740 generated' },
            { id: 'rpt-sal', section: 'Sales & Estimates', status: 'Ready', note: '$38,420 recovered' },
            { id: 'rpt-col', section: 'Collections Report', status: 'Ready', note: '$9,460 AR collected' },
            { id: 'rpt-tec', section: 'Technician Rankings', status: 'Ready', note: 'Torres #1 today' },
            { id: 'rpt-mem', section: 'Membership Growth', status: 'Building', note: 'Compiling renewals' },
            { id: 'rpt-for', section: '30-Day Forecast', status: 'Building', note: 'Weather adjustments' },
          ].map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 py-1">
              <span className="text-xs text-foreground">{s.section}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{s.note}</span>
                <StatusBadge variant={s.status === 'Ready' ? 'complete' : 'pending'} label={s.status} />
              </div>
            </div>
          ))}
        </div>
      </AutomationCard>

      {/* Card 16: Communication Hub */}
      <AutomationCard
        id="card-comms"
        title="Communication Hub"
        owner="Omnichannel AI"
        status="active"
        primaryMetric="482"
        primaryLabel="conversations handled today"
        description="Manages all inbound and outbound communications across phone, SMS, email, and web chat. Books appointments, answers FAQs, and escalates complex issues."
        footerMetrics={[
          { label: 'Booking Rate', value: '34%', color: 'success' },
          { label: 'Appointments Booked', value: '164' },
          { label: 'Escalated to Human', value: '18', color: 'warning' },
        ]}
      >
        <div className="space-y-2">
          {commData.map((ch) => (
            <div key={ch.id} className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground w-16 flex-shrink-0">{ch.channel}</span>
              <div className="flex-1">
                <ProgressBar value={ch.conversations} max={482} color="secondary" />
              </div>
              <span className="text-xs font-tabular text-foreground w-6 text-right">{ch.conversations}</span>
              <span className="text-xs font-semibold text-success font-tabular w-8 text-right">{ch.rate}</span>
            </div>
          ))}
        </div>
      </AutomationCard>

      {/* Card 17: Marketing Attribution */}
      <AutomationCard
        id="card-marketing"
        title="Marketing Attribution"
        owner="Marketing Analytics AI"
        status="complete"
        primaryMetric="142"
        primaryLabel="leads tracked today"
        description="Tracks revenue attribution across all marketing channels. Calculates true ROI per source including lifetime value adjustments for maintenance members."
        footerMetrics={[
          { label: 'Revenue Generated', value: '$86,000', color: 'success' },
          { label: 'Best ROI', value: 'Referral ∞', color: 'success' },
          { label: 'Avg CPL', value: '$97' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'source', label: 'Source', width: '28%' },
            { key: 'leads', label: 'Leads', width: '12%', align: 'right' },
            { key: 'revenue', label: 'Revenue', width: '22%', align: 'right' },
            { key: 'cpl', label: 'CPL', width: '14%', align: 'right' },
            { key: 'roi', label: 'ROI', width: '14%', align: 'right', render: (v) => (
              <span className={`text-xs font-semibold font-tabular ${String(v).includes('∞') ? 'text-success' : parseFloat(String(v)) >= 3 ? 'text-success' : parseFloat(String(v)) >= 2 ? 'text-warning' : 'text-danger'}`}>{String(v)}</span>
            )},
          ]}
          rows={mktgRows}
          maxRows={5}
        />
      </AutomationCard>

      {/* Card 18: Customer Retention Engine */}
      <AutomationCard
        id="card-retention"
        title="Customer Retention Engine"
        owner="Retention AI"
        status="running"
        primaryMetric="$2.4M"
        primaryLabel="dormant customer revenue pool"
        description="Identifies and re-engages dormant customers (12–36 months since last service) through personalized campaigns. Restores memberships and schedules tune-up appointments."
        footerMetrics={[
          { label: 'Recovered This Month', value: '$61,800', color: 'success' },
          { label: 'Reactivations', value: '42' },
          { label: 'Memberships Restored', value: '28', color: 'success' },
        ]}
      >
        <MiniTable
          columns={[
            { key: 'customer', label: 'Customer', width: '22%' },
            { key: 'lastService', label: 'Last Svc', width: '18%' },
            { key: 'campaign', label: 'Stage', width: '16%' },
            { key: 'probability', label: 'Prob%', width: '12%', align: 'right', render: (v) => (
              <span className={`text-xs font-semibold font-tabular ${Number(v) >= 70 ? 'text-success' : Number(v) >= 50 ? 'text-warning' : 'text-danger'}`}>{String(v)}%</span>
            )},
            { key: 'nextAction', label: 'Next Action', width: '28%', render: (v) => (
              <span className="text-xs text-muted-foreground">{String(v)}</span>
            )},
          ]}
          rows={retentionRows}
          maxRows={5}
        />
      </AutomationCard>

    </div>
  );
}